import { useEffect, useRef, useState } from "react";


function buildSectionId(title, index) {
    const normalizedTitle = title
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return normalizedTitle || `section-${index + 1}`;
}

export function useSectionNavigation(content) {
    const [sections, setSections] = useState([]);
    const [activeSection, setActiveSection] = useState("");
    const contentContainerRef = useRef(null);

    const getHeadingElementsWithIds = () => {
        const container = contentContainerRef.current;
        if (!container) {
            return [];
        }

        const headingElements = Array.from(container.querySelectorAll("h2"));

        return headingElements.map((heading, index) => {
            const headingTitle =
                heading.textContent?.trim() || `Section ${index + 1}`;
            const headingId = heading.id || buildSectionId(headingTitle, index);
            heading.id = headingId;

            return {
                element: heading,
                id: headingId,
                title: headingTitle,
            };
        });
    };

    useEffect(() => {
        const contentElement = contentContainerRef.current;
        if (!contentElement) return undefined;

        const headings = getHeadingElementsWithIds();

        if (!headings.length) {
            setSections([]);
            setActiveSection("");
            return undefined;
        }

        const nextSections = headings.map(({ id, title }) => ({ id, title }));

        setSections(nextSections);
        setActiveSection(
            (previousSection) => previousSection || nextSections[0]?.id || "",
        );

        const sectionElements = headings
            .map(({ element }) => element)
            .filter(Boolean);

        if (!sectionElements.length) {
            return undefined;
        }

        const activationOffset = 180;

        const updateActiveSection = () => {
            const elements = getHeadingElementsWithIds().map(
                ({ element }) => element,
            );
            if (!elements.length) return;

            const pageBottomOffset = 4;
            const reachedPageBottom =
                window.innerHeight + window.scrollY
                >= document.documentElement.scrollHeight - pageBottomOffset;

            if (reachedPageBottom) {
                const lastElement = elements[elements.length - 1];
                setActiveSection((prev) =>
                    prev === lastElement.id ? prev : lastElement.id,
                );
                return;
            }

            let activeId = elements[0].id;
            elements.forEach((el) => {
                if (el.getBoundingClientRect().top <= activationOffset) {
                    activeId = el.id;
                }
            });

            setActiveSection((prev) => (prev === activeId ? prev : activeId));
        };

        updateActiveSection();

        let frameId = null;
        const handleScroll = () => {
            if (frameId) {
                return;
            }

            frameId = window.requestAnimationFrame(() => {
                updateActiveSection();
                frameId = null;
            });
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
            if (frameId) window.cancelAnimationFrame(frameId);
        };
    }, [content]);

    const handleNavigationClick = (sectionId) => {
        const container = contentContainerRef.current;

        if (!container) return;

        const escapedSectionId =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
                ? CSS.escape(sectionId)
                : sectionId;

        const targetSection = container
            .querySelector(`#${escapedSectionId}`)
            || getHeadingElementsWithIds().find(({ id }) => id === sectionId)
                ?.element;

        if (!targetSection) return;

        setActiveSection(sectionId);
        targetSection.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return {
        sections,
        activeSection,
        contentContainerRef,
        handleNavigationClick,
    };
}
